import React, { useState, useCallback } from 'react'
import ReactCountryFlag from "react-country-flag"
import Visibility from '@mui/icons-material/Visibility'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import KeyboardDoubleArrowLeft from '@mui/icons-material/KeyboardDoubleArrowLeft'
import KeyboardDoubleArrowRight from '@mui/icons-material/KeyboardDoubleArrowRight'
import NoteEditor from './NoteEditor'
import moment from 'moment'

// Компонент пагинации для мобильной версии
const MobilePaginationActions = ({ page, rowsPerPage, count, onPageChange }) => {
  const safePage = isNaN(page) ? 0 : page
  const safeRowsPerPage = isNaN(rowsPerPage) ? 10 : rowsPerPage
  const safeCount = isNaN(count) ? 0 : count
  
  const totalPages = safeRowsPerPage === 0 ? 0 : Math.ceil(safeCount / safeRowsPerPage)
  
  const handleFirstPage = () => onPageChange(null, 0)
  const handlePrevPage = () => onPageChange(null, Math.max(0, safePage - 1))
  const handleNextPage = () => onPageChange(null, Math.min(totalPages - 1, safePage + 1))
  const handleLastPage = () => onPageChange(null, Math.max(0, totalPages - 1))

  return (
    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
      <button 
        style={{
          background: 'var(--dark-gray)',
          border: '1px solid var(--light-gray)',
          color: 'var(--text-light)',
          padding: '8px',
          borderRadius: 'var(--radius-sm)',
          cursor: safePage === 0 ? 'not-allowed' : 'pointer',
          fontSize: 'var(--text-sm)',
          minWidth: '36px',
          height: '36px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: safePage === 0 ? 0.5 : 1
        }}
        onClick={handleFirstPage}
        disabled={safePage === 0}
      >
        <KeyboardDoubleArrowLeft style={{ fontSize: '14px' }} />
      </button>
      
      <button 
        style={{
          background: 'var(--dark-gray)',
          border: '1px solid var(--light-gray)',
          color: 'var(--text-light)',
          padding: '8px',
          borderRadius: 'var(--radius-sm)',
          cursor: safePage === 0 ? 'not-allowed' : 'pointer',
          fontSize: 'var(--text-sm)',
          minWidth: '36px',
          height: '36px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: safePage === 0 ? 0.5 : 1
        }}
        onClick={handlePrevPage}
        disabled={safePage === 0}
      >
        <KeyboardArrowLeft style={{ fontSize: '14px' }} />
      </button>
      
      <span style={{
        fontSize: 'var(--text-sm)',
        color: 'var(--text-muted)',
        margin: '0 var(--spacing-sm)',
        whiteSpace: 'nowrap',
        minWidth: '80px',
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center'
      }}>
        {isNaN(page) ? 0 : page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, safeCount)} of {safeCount}
      </span>
      
      <button 
        style={{
          background: 'var(--dark-gray)',
          border: '1px solid var(--light-gray)',
          color: 'var(--text-light)',
          padding: '8px',
          borderRadius: 'var(--radius-sm)',
          cursor: safePage >= totalPages - 1 ? 'not-allowed' : 'pointer',
          fontSize: 'var(--text-sm)',
          minWidth: '36px',
          height: '36px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: safePage >= totalPages - 1 ? 0.5 : 1
        }}
        onClick={handleNextPage}
        disabled={safePage >= totalPages - 1}
      >
        <KeyboardArrowRight style={{ fontSize: '14px' }} />
      </button>
      
      <button 
        style={{
          background: 'var(--dark-gray)',
          border: '1px solid var(--light-gray)',
          color: 'var(--text-light)',
          padding: '8px',
          borderRadius: 'var(--radius-sm)',
          cursor: safePage >= totalPages - 1 ? 'not-allowed' : 'pointer',
          fontSize: 'var(--text-sm)',
          minWidth: '36px',
          height: '36px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: safePage >= totalPages - 1 ? 0.5 : 1
        }}
        onClick={handleLastPage}
        disabled={safePage >= totalPages - 1}
      >
        <KeyboardDoubleArrowRight style={{ fontSize: '14px' }} />
      </button>
    </div>
  )
}

const MobileActiveProxiesTable = ({ 
  activeProxiesList, 
  selectedRow, 
  onRowClick, 
  onDetailsClick,
  apiClient
}) => {
  //notes
  const [editingNoteFor, setEditingNoteFor] = useState(null)
  const [notes, setNotes] = useState({})
  
  // Состояние для пагинации
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10) // 10 по умолчанию
  
  // Мемоизируем функцию для вычисления возраста прокси
  const calculateProxyAge = useCallback((firstSeen) => {
    if (!firstSeen) return 'N/A'
    
    const now = moment()
    const firstSeenDate = moment(firstSeen)
    
    const years = now.diff(firstSeenDate, 'years')
    if (years >= 1) return `${years}y`
    
    const months = now.diff(firstSeenDate, 'months')
    if (months >= 1) return `${months}m`
    
    const days = now.diff(firstSeenDate, 'days')
    if (days >= 1) return `${days}d`
    
    const hours = now.diff(firstSeenDate, 'hours')
    return `${hours}h`
  }, [])
  
  // Сортируем прокси по оставшемуся времени (больше времени сверху) для мобильной версии
  const sortedActiveProxiesList = activeProxiesList.sort((a, b) => {
    // Сортируем по времени истечения в убывающем порядке (больше времени сверху)
    const timeA = a.until || 0
    const timeB = b.until || 0
    return timeB - timeA
  })

  // Обработчики для пагинации
  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  // Получаем данные для текущей страницы
  const getCurrentPageData = () => {
    const startIndex = page * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    return sortedActiveProxiesList.slice(startIndex, endIndex)
  }

  const handleEditNote = (agentId) => {
    setEditingNoteFor(agentId)
  }

  const handleSaveNote = (agentId, note) => {
    setNotes(prev => ({ ...prev, [agentId]: note }))
    setEditingNoteFor(null)
  }

  const handleCancelNote = () => {
    setEditingNoteFor(null)
  }
  const formatTimeLeft = (date) => {
    const diffMs = date - Date.now()
    const minutes = Math.max(0, Math.floor(diffMs / (60 * 1000) % 60))
    const hours = Math.floor(diffMs / (60 * 60 * 1000))
  
    let result = minutes + "m"
    if (hours > 0) {
      result = hours + "h " + result
    }
  
    if (result === "0m") {
      result = "now"
    }
  
    return result
  }

  return (
    <div style={{ padding: 'var(--spacing-sm)' }}>
      {getCurrentPageData().map((row, index) => (
        <div
          key={row?.id || index}
          onClick={() => onRowClick(row)}
          style={{
            background: 'var(--medium-gray)',
            border: '1px solid var(--light-gray)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-md)',
            marginBottom: 'var(--spacing-sm)',
            cursor: 'pointer',
            borderLeft: selectedRow?.id === row?.id ? '4px solid var(--primary-orange)' : '4px solid transparent',
            transition: 'var(--transition-normal)'
          }}
        >
          {/* Заголовок карточки */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 'var(--spacing-md)'
          }}>
            <div>
              <div style={{
                fontSize: 'var(--text-lg)',
                fontWeight: '600',
                color: 'var(--text-light)',
                marginBottom: 'var(--spacing-xs)'
              }}>
                {row?.info?.ip}
              </div>
              <div style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-muted)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-xs)'
              }}>
                <ReactCountryFlag 
                  countryCode={row?.info?.loc?.cc} 
                  svg 
                  style={{width: '16px', height: '12px'}}
                />
                <span>{row?.info?.loc?.cc}</span>
                <span>•</span>
                <span>{row?.info?.loc?.city}, {row?.info?.loc?.reg}</span>
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation()
                onDetailsClick(row, e)
              }}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--primary-orange)',
                padding: '8px',
                borderRadius: 'var(--radius-sm)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(255, 152, 0, 0.1)'
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent'
              }}
            >
              <Visibility style={{ fontSize: '24px' }} />
            </button>
          </div>

          {/* Детали прокси */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 'var(--spacing-md)',
            marginBottom: 'var(--spacing-md)'
          }}>
            <div>
              <div style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginBottom: '4px',
                fontWeight: '500'
              }}>
                ISP
              </div>
              <div style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-light)',
                wordBreak: 'break-word'
              }}>
                {row?.info?.loc?.isp}
              </div>
            </div>
            
            <div>
              <div style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginBottom: '4px',
                fontWeight: '500'
              }}>
                ZIP Code
              </div>
              <div style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-light)'
              }}>
                {row?.info?.loc?.zip || 'N/A'}
              </div>
            </div>
            
            <div>
              <div style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginBottom: '4px',
                fontWeight: '500'
              }}>
                Age/Activity
              </div>
              <div style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-light)'
              }}>
                {row?.info?.probeActv !== undefined ? (
                  <span style={{ 
                    color: 'var(--primary-orange)',
                    fontWeight: '600'
                  }}>
                    {calculateProxyAge(row?.info?.firstSeen)}/{Math.round(row?.info?.probeActv * 100)}%
                  </span>
                ):(
                  <span style={{ color: 'var(--text-muted)' }}>N/A</span>
                )}
              </div>
            </div>
            
            <div>
              <div style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginBottom: '4px',
                fontWeight: '500'
              }}>
                Users
              </div>
              <div style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-light)'
              }}>
                {row?.currentInfo?.leases?.count || row?.info?.leases?.count || 0}
              </div>
            </div>
          </div>

          {/* Время истечения */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 'var(--spacing-sm)',
            background: 'var(--dark-gray)',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--light-gray)'
          }}>
            <div>
              <div style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginBottom: '2px'
              }}>
                Expires in
              </div>
              <div style={{
                fontSize: 'var(--text-sm)',
                fontWeight: '600',
                color: 'var(--primary-orange)'
              }}>
                {formatTimeLeft(row?.until)}
              </div>
            </div>
            <div style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--text-muted)',
              textAlign: 'right'
            }}>
              <div>Proxy ID: {row?.id}</div>
            </div>
          </div>

          {/* Заметка */}
          {editingNoteFor === row?.agentId ? (
            <NoteEditor
              proxy={row}
              apiClient={apiClient}
              onSave={(note) => handleSaveNote(row?.agentId, note)}
              onCancel={handleCancelNote}
            />
          ) : (
            <div style={{
              marginTop: 'var(--spacing-sm)',
              padding: 'var(--spacing-sm)',
              background: 'var(--dark-gray)',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--light-gray)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--spacing-xs)'
              }}>
                <span style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--text-muted)',
                  fontWeight: '500'
                }}>
                  📝 Note
                </span>
                <button
                  onClick={() => handleEditNote(row?.agentId)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--primary-orange)',
                    cursor: 'pointer',
                    fontSize: 'var(--text-sm)',
                    padding: '4px 6px',
                    borderRadius: 'var(--radius-sm)',
                    transition: 'var(--transition-normal)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'var(--medium-gray)'
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent'
                  }}
                >
                  ✏️
                </button>
              </div>
              <div style={{
                fontSize: 'var(--text-sm)',
                color: notes[row?.agentId] || row?.note ? 'var(--text-light)' : 'var(--text-muted)',
                fontStyle: notes[row?.agentId] || row?.note ? 'normal' : 'italic',
                lineHeight: '1.4'
              }}>
                {(notes[row?.agentId] || row?.note || 'No note added').length > 10 
                    ? (notes[row?.agentId] || row?.note || 'No note added').substring(0, 10) + '...'
                    : (notes[row?.agentId] || row?.note || 'No note added')
                }
              </div>
            </div>
          )}
        </div>
      ))}
      
      {/* Пагинация для мобильной версии */}
      {sortedActiveProxiesList.length > rowsPerPage && (
        <div style={{
          marginTop: 'var(--spacing-lg)',
          padding: 'var(--spacing-md)',
          background: 'var(--medium-gray)',
          border: '1px solid var(--light-gray)',
          borderRadius: 'var(--radius-lg)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-md)',
          alignItems: 'center'
        }}>
          {/* Строка с количеством строк на странице */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 'var(--spacing-sm)' 
          }}>
            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
              Rows per page:
            </span>
            <select 
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              style={{
                background: 'var(--dark-gray)',
                border: '1px solid var(--light-gray)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-light)',
                padding: '6px 8px',
                fontSize: 'var(--text-sm)',
                minWidth: '60px'
              }}
            >
              {[10, 20, 30, 40, 50].map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          
          {/* Навигация */}
          <MobilePaginationActions 
            page={page}
            rowsPerPage={rowsPerPage}
            count={sortedActiveProxiesList.length}
            onPageChange={handleChangePage}
          />
        </div>
      )}
    </div>
  )
}

export default MobileActiveProxiesTable
